/**
 * ClickChutney Simple Script Integration
 * Zero dependencies, no hooks, React 19 compatible
 */

import React from 'react';
import type { ClickChutneyConfig } from './types';

/**
 * Simple ClickChutney Provider - No hooks, just script injection
 * 
 * @example
 * ```tsx
 * import { ClickChutneyProvider } from 'clickchutney-react';
 * 
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <ClickChutneyProvider
 *           config={{
 *             trackingId: "your-tracking-id",
 *             endpoint: "http://localhost:3000"
 *           }}
 *         >
 *           {children}
 *         </ClickChutneyProvider>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function ClickChutneyProvider({ 
  children, 
  config 
}: { 
  children: React.ReactNode; 
  config: ClickChutneyConfig;
}) {
  // Only run on client side
  if (typeof window !== 'undefined') {
    // Use a namespace for internal state
    (window as any).__cc = (window as any).__cc || {};
    const __cc = (window as any).__cc;

    // Check if already initialized to prevent double initialization
    if (!__cc.initialized) {
      __cc.initialized = true;

      // Visitor ID (persistent across sessions using localStorage if available)
      let visitorId = null;
      try {
        visitorId = localStorage.getItem('__cc_vid');
        if (!visitorId) {
          visitorId = 'ccv_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
          localStorage.setItem('__cc_vid', visitorId);
        }
      } catch (e) {
        // Ignore if localStorage not available
      }

      // Session ID (new per page load/tab, stored on window)
      const sessionId = 'ccs_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

      // Set up queue for early calls
      (window as any).cc = (window as any).cc || function(...args: any[]) {
        ((window as any).cc.q = (window as any).cc.q || []).push(args);
      };
      (window as any).cc.q = (window as any).cc.q || [];

      // Define the tracking function
      const track = (...args: any[]) => {
        const endpoint = config.endpoint 
          ? `${config.endpoint}/api/analytics/collect`
          : 'https://api.clickchutney.com/api/analytics/collect';

        const [type = 'event', eventName, properties = {}] = args;
        
        const payload = {
          type,
          event: eventName,
          properties,
          trackingId: config.trackingId,
          visitorId: visitorId,
          sessionId,
          timestamp: Date.now(),
          url: window.location.href,
          page: window.location.pathname + window.location.search,
          title: document.title,
          referrer: document.referrer || null,
          userAgent: navigator.userAgent,
          screen: {
            width: window.screen.width,
            height: window.screen.height
          },
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          language: navigator.language
        };

        if (config.debug) {
          console.log('🍛 ClickChutney:', payload);
        }

        // Prefer sendBeacon for better performance and reliability
        const jsonPayload = JSON.stringify(payload);
        const blob = new Blob([jsonPayload], { type: 'application/json' });
        if (navigator.sendBeacon && navigator.sendBeacon(endpoint, blob)) {
          // Sent successfully via beacon
        } else {
          // Fallback to fetch
          fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: jsonPayload,
            keepalive: true
          }).catch(error => {
            if (config.debug) {
              console.error('🍛 ClickChutney Error:', error);
            }
          });
        }
      };

      // Process any queued events
      const q = (window as any).cc.q.slice(); // Copy queue
      (window as any).cc = track;
      for (const item of q) {
        track(...item);
      }

      // Auto-track pageviews if enabled (with SPA support)
      if (config.autoPageview !== false) {
        // Track initial pageview
        setTimeout(() => {
          (window as any).cc('pageview', window.location.pathname + window.location.search, { title: document.title });
        }, 0);

        // Override history for SPA navigation (e.g., Next.js App Router)
        const originalPushState = history.pushState;
        history.pushState = function(...args) {
          originalPushState.apply(this, args);
          (window as any).cc('pageview', window.location.pathname + window.location.search, { title: document.title });
        };

        const originalReplaceState = history.replaceState;
        history.replaceState = function(...args) {
          originalReplaceState.apply(this, args);
          (window as any).cc('pageview', window.location.pathname + window.location.search, { title: document.title });
        };

        // Handle back/forward navigation
        window.addEventListener('popstate', () => {
          (window as any).cc('pageview', window.location.pathname + window.location.search, { title: document.title });
        });
      }
    }
  }

  return <>{children}</>;
}